// Cliente del staff de empresa (fase 2). Contrato: docs/CONTRATO-API-FASE2.md.
//
// El token del staff vive aparte del de empresa (`staff-token`). Los permisos
// NO se leen del claim del token sino de GET /api/auth/staff/me.

export const STAFF_TOKEN_KEY = 'staff-token';

export const SESSION_INVALID_MESSAGE =
  'Tu acceso fue revocado o tu sesión ya no es válida. Inicia sesión de nuevo.';

export const SESSION_EXPIRED_MESSAGE = 'Tu sesión venció. Inicia sesión de nuevo para continuar.';

export const getStaffToken = () => {
  try {
    return localStorage.getItem(STAFF_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

export const setStaffToken = (token) => {
  try {
    localStorage.setItem(STAFF_TOKEN_KEY, token);
  } catch (e) {
    // sin localStorage la sesión dura lo que dure la pestaña (no se guarda)
  }
};

export const clearStaffToken = () => {
  try {
    localStorage.removeItem(STAFF_TOKEN_KEY);
  } catch (e) {
    // nada que limpiar
  }
};

// Error con el cuerpo del backend: `status`, `code` y los campos extra
// (`scannedAt`, `scannedBy`, `invalidActivityIds`…) quedan en `data`.
export class ApiError extends Error {
  constructor(message, { status = 0, code = null, data = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// La sesión de staff ya no sirve (revocado, contraseña cambiada, empresa
// eliminada, token anterior a una reactivación o vencido): el token ya se borró
// y hay que mandar al login de staff.
export class StaffSessionError extends ApiError {
  constructor(message = SESSION_INVALID_MESSAGE, opts = {}) {
    super(message, opts);
    this.name = 'StaffSessionError';
  }
}

export const NETWORK_ERROR_MESSAGE = 'Error de conexión. Revisa tu internet e inténtalo de nuevo.';

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

// Petición con el token de staff. Lanza StaffSessionError si el backend
// responde `code: staff_session_invalid` (y, por un token vencido o roto, 401
// en general: el login es la única salida), ApiError en cualquier otro fallo.
export const staffFetch = async (path, { method = 'GET', body } = {}) => {
  const token = getStaffToken();
  const headers = { Authorization: `Bearer ${token}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(NETWORK_ERROR_MESSAGE);
  }

  const data = await readJson(response);

  if (response.status === 401) {
    clearStaffToken();
    // JWT vencido: 401 {"msg": "Token has expired"}, sin `code` ni `error`.
    const expired = Boolean(data && !data.code && typeof data.msg === 'string' && /expired/i.test(data.msg));
    throw new StaffSessionError(
      data && data.code === 'staff_session_invalid'
        ? (data.error || SESSION_INVALID_MESSAGE)
        : expired ? SESSION_EXPIRED_MESSAGE : SESSION_INVALID_MESSAGE,
      { status: 401, code: data && data.code, data }
    );
  }

  if (!response.ok || !data || data.success === false) {
    throw new ApiError(
      (data && data.error) || 'Ocurrió un error. Inténtalo de nuevo.',
      { status: response.status, code: data && data.code, data }
    );
  }
  return data;
};

// POST /api/auth/staff/login (sin token). Devuelve { token, staff, empresa }.
export const staffLogin = async (email, password) => {
  let response;
  try {
    response = await fetch('/api/auth/staff/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    throw new ApiError(NETWORK_ERROR_MESSAGE);
  }
  const data = await readJson(response);

  if (response.status === 429) {
    throw new ApiError('Demasiados intentos. Espera un minuto e inténtalo de nuevo.', { status: 429 });
  }
  if (!response.ok || !data || !data.success) {
    throw new ApiError(
      (data && data.error) || 'No se pudo iniciar sesión. Inténtalo de nuevo.',
      { status: response.status, code: data && data.code, data }
    );
  }
  return data;
};

// GET /api/auth/staff/me: rol y actividades actuales, leídos de la BD.
export const fetchStaffMe = () => staffFetch('/api/auth/staff/me');
